// src/components/Admin/SellerManagement/Notes/AdminNotesSection.jsx
import React, { useState, useEffect, useCallback }  from 'react';
import {
  getAdminNotes,
  createAdminNote,
  updateAdminNote,
  deleteAdminNote
} from '../../../../services/AdminApis'; // Adjust path as needed
import { useToast } from "../../../../hooks/use-toast";
import AddNoteForm from './AddNoteForm';
import NoteItem from './NoteItem';

const AdminNotesSection = ({ contextType, contextId, contextTab }) => {
  const [notes, setNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const { toast } = useToast();

  const fetchNotes = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getAdminNotes({
        context_type: contextType,
        context_id: contextId,
        context_tab: contextTab
      });
      setNotes(data);
    } catch (err) {
      setError("Failed to load notes. You may not have permission to view them.");
      toast({ title: "Failed to load notes", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  }, [contextType, contextId, contextTab, toast]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const handleAddNote = async (content) => {
    try {
      setIsSubmitting(true);
      const newNote = await createAdminNote({
        content,
        context_type: contextType,
        context_id: contextId,
        context_tab: contextTab
      });
      // Add new note to the top of the list
      setNotes(prev => [newNote, ...prev]);
      toast({ title: "Note added successfully" });
    } catch (err) {
      toast({ title: "Failed to add note", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateNote = async (noteId, content) => {
    const updatedNote = await updateAdminNote(noteId, content);
    setNotes(prev =>
      prev.map(note => (note.id === noteId ? updatedNote : note))
    );
    return updatedNote; // Return to NoteItem to stop loading
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await deleteAdminNote(noteId);
      setNotes(prev => prev.filter(note => note.id !== noteId));
      toast({ title: "Note deleted successfully" });
    } catch (err) {
      toast({ title: "Failed to delete note", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-2 pt-2 border-t">
      <h3 className="text-lg font-semibold">Admin Notes ({notes.length})</h3>
      <AddNoteForm onSubmit={handleAddNote} isLoading={isSubmitting} />

      <div className="space-y-4 mt-4">
        {isLoading && <p>Loading notes...</p>}
        {error && <p className="text-destructive">{error}</p>}
        {!isLoading && !error && notes.length === 0 && (
          <p className="text-sm text-muted-foreground">No notes for this section yet.</p>
        )}
        {!isLoading && !error && notes.map(note => (
          <NoteItem
            key={note.id}
            note={note}
            onUpdate={handleUpdateNote}
            onDelete={handleDeleteNote}
          />
        ))}
      </div>
    </div>
  );
};

export default AdminNotesSection;